/**
 * Everything the shell is told, read once, in one place.
 *
 * `import.meta.env` is a build-time substitution, so this is a constant folded into the
 * bundle rather than a runtime lookup. That has one consequence worth stating plainly:
 * changing the issuer or the API base means rebuilding, not restarting. A runtime-config
 * endpoint would avoid that, and would also mean the service serving an unauthenticated
 * JSON document describing its own identity provider — which is fine, and is the thing to
 * add if a customer needs one image across several environments.
 */

export type AuthMode = 'redirect' | 'manual';

export interface ShellConfig {
  /** Where the module's router is mounted, as the browser sees it. */
  apiBaseUrl: string;
  /** The router basename, from Vite's `base`. */
  basePath: string;
  authMode: AuthMode;
  authUrl: string;
  clientId: string;
  /** Only sent on the login URL when explicitly configured. Always shown in diagnostics. */
  audience: string | null;
  sapiensUrl: string | null;
  crmUrl: string | null;
}

function read(name: string): string | null {
  const raw: unknown = import.meta.env[name];
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  return value.length > 0 ? value : null;
}

function readAuthMode(): AuthMode {
  const raw = read('VITE_AUTH_MODE');
  // Anything unrecognised falls back to the real flow rather than to the dev hatch.
  // Getting that backwards would let a typo ship a build that never authenticates.
  return raw === 'manual' ? 'manual' : 'redirect';
}

/** Vite normalises `base` to a value with a leading and trailing slash. */
function basePath(): string {
  const base = import.meta.env.BASE_URL || '/';
  return base.length > 1 ? base.replace(/\/$/, '') : '/';
}

export const SHELL_CONFIG: ShellConfig = {
  apiBaseUrl: read('VITE_INVENTORY_API_BASE_URL') ?? '/inventory',
  basePath: basePath(),
  authMode: readAuthMode(),
  authUrl: read('VITE_AUTH_URL') ?? 'https://auth.xpertte.com',
  clientId: read('VITE_AUTH_CLIENT_ID') ?? 'inai-inventory',
  audience: read('VITE_AUTH_AUDIENCE'),
  sapiensUrl: read('VITE_SAPIENS_URL'),
  crmUrl: read('VITE_CRM_URL'),
};
